import { useState } from 'react'
import { Library, Search } from 'lucide-react'
import { useLibrary } from '@/hooks/useLibrary'
import { DocumentCard } from '@/components/library/DocumentCard'
import { LibraryFilters } from '@/components/library/LibraryFilters'

export function LibraryScreen() {
  const {
    documents,
    loading,
    filters,
    taxonomies,
    hasMore,
    toggleTaxonomy,
    toggleDocumentType,
    setSearch,
    clearFilters,
    loadMore,
  } = useLibrary()

  const [searchInput, setSearchInput] = useState('')

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput || undefined)
  }

  const hasActiveFilters = !!(filters.taxonomyIds.length > 0 || filters.documentTypes.length > 0 || filters.search)

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex-shrink-0 bg-brand-surface border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-2">
            <Library size={24} className="text-brand-primary" />
            <h1 className="text-2xl font-bold text-brand-text">Biblioteca Técnica</h1>
          </div>
          <p className="text-brand-text-secondary text-sm mb-4">
            Acervo de documentos científicos e técnicos sobre agricultura regenerativa
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-xl">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-brand-text-muted" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Buscar documentos por título, autor, assunto..."
                className="w-full pl-11 pr-4 py-2.5 rounded-xl bg-brand-bg border border-brand-border text-sm text-brand-text placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
              />
            </div>
          </form>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto bg-brand-bg scrollbar-thin pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Filters sidebar (desktop) + mobile sheet */}
            <LibraryFilters
              taxonomies={taxonomies}
              selectedTaxonomyIds={filters.taxonomyIds}
              selectedDocumentTypes={filters.documentTypes}
              onTaxonomyToggle={toggleTaxonomy}
              onDocumentTypeToggle={toggleDocumentType}
              onClearAll={() => {
                clearFilters()
                setSearchInput('')
              }}
              hasActiveFilters={hasActiveFilters}
            />

            {/* Document Grid */}
            <div className="flex-1 min-w-0">
              {/* Loading state */}
              {loading && documents.length === 0 && (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-primary border-t-transparent mx-auto" />
                  <p className="mt-4 text-brand-text-muted text-sm">Carregando biblioteca...</p>
                </div>
              )}

              {/* Documents grid */}
              {(!loading || documents.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {documents.map(doc => (
                    <DocumentCard key={doc.id} document={doc} />
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!loading && documents.length === 0 && (
                <div className="text-center py-12">
                  <Library className="h-12 w-12 mx-auto text-brand-text-muted/30 mb-3" />
                  <p className="text-brand-text-muted">Nenhum documento encontrado.</p>
                  {hasActiveFilters && (
                    <button
                      onClick={() => {
                        clearFilters()
                        setSearchInput('')
                      }}
                      className="mt-3 text-sm text-brand-primary hover:underline"
                    >
                      Limpar filtros
                    </button>
                  )}
                </div>
              )}

              {/* Load more */}
              {hasMore && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                  >
                    {loading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Carregando...
                      </>
                    ) : (
                      'Carregar Mais'
                    )}
                  </button>
                </div>
              )}

              {/* End of list */}
              {!hasMore && documents.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-brand-text-muted text-sm">
                    Você já viu todos os documentos disponíveis!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
